'use client'
import React, { useImperativeHandle, useRef, useEffect } from 'react'
import { gsap } from 'gsap'
import { SplitText } from 'gsap/dist/SplitText'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import { cn } from '../../helpers/utils.js'
import { Heading, Text } from '../../components/index.js'
import { withLayout } from '../../shared/Styles.js'
import type { SloganProps } from './types.js'
import { useInView } from 'react-intersection-observer'

export const componentType = 'copy'
export const componentName = 'brand_slogan'

function chunkArray(array: { title: string }[] = [], size: number = 3) {
  const result = []
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size))
  }
  return result
}

const Slogan = React.forwardRef<HTMLDivElement, SloganProps>(({ data, className = '', ...rest }, ref) => {
  const { title, features = [], tag = 'h1', featureChunkSize = 3 } = data
  const [groupIndex, setGroupIndex] = React.useState(0)
  const [isTransitioning, setIsTransitioning] = React.useState(true)
  const innerRef = useRef<HTMLDivElement>(null)
  const scrollTriggerRef = useRef<ScrollTrigger | null>(null)
  const featuresTriggerRef = useRef<ScrollTrigger | null>(null)
  const featuresGroups = chunkArray(features, featureChunkSize)
  const totalGroups = featuresGroups.length
  const needCarousel = features.length > featureChunkSize
  const displayGroups = needCarousel ? [...featuresGroups, featuresGroups[0]] : featuresGroups
  const groupHeight = 40

  useImperativeHandle(ref, () => innerRef.current as HTMLDivElement)

  const { ref: inViewRef, inView } = useInView()

  // 只在需要轮播时启用定时器
  React.useEffect(() => {
    if (!needCarousel) return
    const timer = setInterval(() => {
      setGroupIndex(i => i + 1)
      setIsTransitioning(true)
    }, 3000)
    return () => clearInterval(timer)
  }, [totalGroups, needCarousel])

  // 只在需要轮播时处理无缝跳转
  React.useEffect(() => {
    if (!needCarousel) return
    if (groupIndex === totalGroups) {
      const handle = setTimeout(() => {
        setIsTransitioning(false)
        setGroupIndex(0)
      }, 500) // 500ms等于动画时长
      return () => clearTimeout(handle)
    } else {
      setIsTransitioning(true)
    }
  }, [groupIndex, totalGroups, needCarousel])

  // 标题动画逻辑保持不变
  const titleRef = useRef<HTMLHeadingElement>(null)
  const featuresRef = useRef<HTMLDivElement>(null)
  const splitTextInstance = useRef<SplitText | null>(null)

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger, SplitText)

    function gsapResize() {
      if (!titleRef.current) return
      splitTextInstance.current = new SplitText(titleRef.current, {
        type: 'words',
        wordsClass: 'word',
      })
      const height = titleRef.current?.clientHeight || 100

      const words = splitTextInstance.current.words
      gsap.set(words, { opacity: 0 })
      scrollTriggerRef.current = ScrollTrigger.create({
        trigger: titleRef.current,
        start: 'bottom bottom-=4%',
        end: `bottom+=${height * 1.5}px bottom-=4%`,
        scrub: true,
        onUpdate: (self: any) => {
          const progress = self.progress
          const total = words.length
          const interval = 1 / total
          const overlap = 0.5
          words.forEach((word: any, i: number) => {
            const start = (i / total) * (1 - overlap)
            const width = interval * (1 + overlap)
            let opacity = (progress - start) / width
            opacity = Math.max(0, Math.min(opacity, 1))
            gsap.set(word, { opacity })
          })
        },
      })

      if (features && features.length > 0) {
        featuresTriggerRef.current = ScrollTrigger.create({
          trigger: featuresRef.current,
          start: `bottom+=${height * 2}px bottom-=6%`,
          end: `bottom+=${height * 2 + 100}px bottom-=6%`,
          onUpdate: (self: any) => {
            gsap.set(featuresRef.current, { opacity: self.progress })
          },
        })
      }
    }

    if (inView) {
      gsapResize()
    }

    return () => {
      splitTextInstance.current && splitTextInstance.current.revert()
      // ScrollTrigger.getAll().forEach((t: any) => t.kill())
      scrollTriggerRef.current && scrollTriggerRef.current.kill()
      featuresTriggerRef.current && featuresTriggerRef.current.kill()
    }
  }, [inView])

  return (
    <div ref={inViewRef}>
      <div
        ref={innerRef}
        {...rest}
        className={cn(
          'slogan-container laptop:flex-row laptop:items-end laptop:justify-between text-info-primary flex h-auto w-full flex-col items-start justify-center gap-[24px] text-center',
          className
        )}
      >
        <Heading
          ref={titleRef}
          as={tag}
          size={2}
          weight={'bold'}
          align={'left'}
          className={cn(
            'slogan-title laptop:text-[40px] desktop:text-[48px] lg-desktop:text-[64px] text-[40px] !leading-[1.1]'
          )}
          html={title}
        />
        {features && features.length > 0 && (
          <div className={cn('relative w-full max-w-[500px] overflow-hidden', `tablet:h-[${groupHeight}px]`)}>
            <div
              className={cn([
                'flex flex-col',
                isTransitioning
                  ? 'transition-transform duration-500 ease-[cubic-bezier(.4,0,.2,1)]'
                  : '!transition-none',
              ])}
              style={{
                transform: `translateY(-${groupIndex * groupHeight}px)`,
              }}
            >
              {displayGroups.map((group, idx) => (
                <div
                  className={cn(
                    'laptop:justify-end flex w-full flex-row items-stretch gap-3 overflow-hidden',
                    `tablet:h-[${groupHeight}px]`
                  )}
                  key={idx}
                >
                  {group.map((feature, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <div className="slogan-feature-divider w-px self-stretch bg-[#E4E5E6]" />}
                      <Text
                        size={3}
                        align="left"
                        as="p"
                        className="slogan-feature-text laptop:text-[14px] line-clamp-3 text-[12px] leading-[1.4]"
                        html={feature.title}
                      />
                    </React.Fragment>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

Slogan.displayName = 'Slogan'

export default withLayout(Slogan)
